import React, { useContext } from 'react'
import PropsContext, { UIKitUser } from '../PropsContext'
import RtcContext from '../RtcContext'

type UserRole = 'doctor' | 'patient'

interface BadgeProps {
  user: UIKitUser
  isMaxView?: boolean
}

/**
 * Badge component that displays role (Doctor/Patient) based on user UID and local role
 * - If user.uid === 0 (local user): Shows local role with "You" prefix
 * - If user.uid !== 0 (remote user): Shows opposite role
 * Automatically updates when screens are swapped via MaxUidContext/MinUidContext
 */
const Badge: React.FC<BadgeProps> = ({ user, isMaxView = false }) => {
  const { styleProps } = useContext(PropsContext)
  const { localUid } = useContext(RtcContext)

  // Get local role from styleProps (passed from App.tsx)
  const localRole = styleProps?.localRole as UserRole | undefined

  if (!localRole) {
    // If role not provided, don't show badge
    return null
  }

  // Determine if this is the local user
  // uid 0 is always the local user in Agora UIKit
  const isLocalUser =
    user.uid === 0 ||
    (localUid?.current !== undefined &&
      String(user.uid) === String(localUid.current))

  // Remote user always has the opposite role
  const remoteRole: UserRole = localRole === 'doctor' ? 'patient' : 'doctor'

  // Determine badge text: the participant's real name when known, the role label otherwise.
  const badgeRole = isLocalUser ? localRole : remoteRole
  const roleLabel = badgeRole === 'doctor' ? 'Doctor' : 'Patient'
  const localName = styleProps?.localName?.trim()
  const remoteName = styleProps?.remoteName?.trim()
  const badgeText = isLocalUser
    ? localName
      ? `${localName} (You)`
      : `You (${roleLabel})`
    : remoteName || roleLabel

  // Badge styles - different for max and min views
  const badgeStyles: React.CSSProperties = isMaxView
    ? {
        position: 'absolute',
        bottom: 28,
        left: 36,
        zIndex: 100,
        padding: '6px 12px',
        borderRadius: 999,
        background:
          'linear-gradient(135deg, rgba(20,184,166,0.85), rgba(45,212,191,0.85))',
        color: '#06202a',
        fontSize: 13,
        fontWeight: 700,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }
    : {
        position: 'absolute',
        bottom: 8,
        left: 8,
        zIndex: 100,
        // Small views are sized to their stream, so they can be narrow: keep the label on one
        // line inside the tile instead of wrapping over the picture.
        maxWidth: 'calc(100% - 16px)',
        boxSizing: 'border-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: '4px 8px',
        borderRadius: 999,
        background: 'rgba(15,23,42,0.7)',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        border: '1px solid rgba(148,163,184,0.25)',
        boxShadow: '0 4px 10px rgba(2,6,23,0.35)'
      }

  return (
    <div style={badgeStyles} title={badgeText}>
      {badgeText}
    </div>
  )
}

export default Badge
